import React, { Dispatch, SetStateAction, useState } from 'react';

import InputLabel from '../../../inputfields/InputLabel';
import IProduct from '../../../../models/product';
import InputTextField from 'components/inputfields/InputTextField';
import FloatInput from '../FloatInput';
import FormHelperText from '@material-ui/core/FormHelperText';
import useDebouncedStateString from 'hooks/useDebouncedState';
import { isBlank } from 'utils/helpers-ts';
import ThreeWaySwitch from 'components/inputfields/ThreeWaySwitch';

interface IWhiteSnusProps {
  readOnly?: boolean;
  approve: boolean;
  isCreatingNew?: boolean;
  state: IProduct;
  setState: Dispatch<SetStateAction<IProduct>>;
}

export const WhiteSnus = ({ readOnly, approve, isCreatingNew, state, setState }: IWhiteSnusProps) => {
  const [whiteSnusNicotinePerPortion, onNicotinePerPortionChange] = useDebouncedStateString(
    String(state?.whiteSnusNicotinePerPortion ?? ''),
    setState,
    'whiteSnusNicotinePerPortion'
  );

  const [whiteSnusNicotinePerGram, onNicotinePerGramChange] = useDebouncedStateString(
    String(state?.whiteSnusNicotinePerGram ?? ''),
    setState,
    'whiteSnusNicotinePerGram'
  );

  const [whiteSnusNicotineStrength, onNicotineStrengthChange] = useDebouncedStateString(
    String(state?.whiteSnusNicotineStrength || ''),
    setState,
    'whiteSnusNicotineStrength'
  );

  const [whiteSnusFlavor, onFlavorChange] = useDebouncedStateString(
    String(state?.whiteSnusFlavor || ''),
    setState,
    'whiteSnusFlavor'
  );

  const [whiteSnusPortionsPerUnit, onPortionsPerUnitChange] = useDebouncedStateString(
    String(state?.whiteSnusPortionsPerUnit ?? ''),
    setState,
    'whiteSnusPortionsPerUnit'
  );

  const [whiteSnusWeightPerPortion, onWeightPerPortionChange] = useDebouncedStateString(
    String(state?.whiteSnusWeightPerPortion ?? ''),
    setState,
    'whiteSnusWeightPerPortion'
  );

  const [whiteSnusFormat, onFormatChange] = useDebouncedStateString(
    String(state?.whiteSnusFormat || ''),
    setState,
    'whiteSnusFormat'
  );

  const [focusedFields, setFocusedFfields] = useState<Record<string, boolean>>({});

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocusedFfields({ ...focusedFields, [event.target.name]: true });
  };

  const styles = { marginBottom: 0, width: 200, flex: 'none' };

  const whiteSnusNicotinePerPortionError =
    (!isCreatingNew || focusedFields.whiteSnusNicotinePerPortion) && isBlank(whiteSnusNicotinePerPortion)
      ? 'Vänligen fyll i detta fält eller spara som utkast'
      : '';
  const whiteSnusNicotinePerGramError =
    (!isCreatingNew || focusedFields.whiteSnusNicotinePerGram) && isBlank(whiteSnusNicotinePerGram)
      ? 'Vänligen fyll i detta fält eller spara som utkast'
      : '';

  return (
    <>
      <h3>Specifikation</h3>

      <div style={{ width: '100%', marginBottom: '5px' }}>
        <InputLabel required heading="Nikotin per portion" text="Mängden nikotin per portion angivet i milligram." />
        <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
          <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center', flexGrow: 1 }}>
            <FloatInput<IProduct>
              readOnly={readOnly}
              dense={false}
              required
              hideErrorText
              forceRed={!!whiteSnusNicotinePerPortionError}
              state={state}
              label="Nikotin per portion"
              name="whiteSnusNicotinePerPortion"
              value={whiteSnusNicotinePerPortion}
              onChange={event => onNicotinePerPortionChange(event.target.value)}
              onBlur={handleBlur}
              style={styles}
            />
            <h3 style={{ margin: 0 }}>&nbsp;mg</h3>
          </div>
          {approve && state?.approve?.find(x => x.field === 'whiteSnusNicotinePerPortion') && (
            <ThreeWaySwitch
              readOnly={readOnly}
              name='whiteSnusNicotinePerPortion'
              state={state}
              setState={setState}
            />
          )}
        </div>
        <FormHelperText error={!!whiteSnusNicotinePerPortionError} style={{ marginLeft: 14 }}>
          {whiteSnusNicotinePerPortionError ||
            'Obligatoriskt för att aktivera produkten, men kan sparas som utkast utan att fyllas i.'}
        </FormHelperText>
      </div>

      <div style={{ width: '100%', marginBottom: '5px' }}>
        <InputLabel required heading="Nikotin per gram" text="Mängden nikotin per gram angivet i milligram." />
        <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
          <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center', flexGrow: 1 }}>
            <FloatInput<IProduct>
              readOnly={readOnly}
              dense={false}
              required
              hideErrorText
              forceRed={!!whiteSnusNicotinePerGramError}
              state={state}
              label="Nikotin per gram"
              name="whiteSnusNicotinePerGram"
              value={whiteSnusNicotinePerGram}
              onChange={event => onNicotinePerGramChange(event.target.value)}
              onBlur={handleBlur}
              style={styles}
            />
            <h3 style={{ margin: 0 }}>&nbsp;mg/g</h3>
          </div>
          {approve && state?.approve?.find(x => x.field === 'whiteSnusNicotinePerGram') && (
            <ThreeWaySwitch
              readOnly={readOnly}
              name='whiteSnusNicotinePerGram'
              state={state}
              setState={setState}
            />
          )}
        </div>
        <FormHelperText error={!!whiteSnusNicotinePerGramError} style={{ marginLeft: 14 }}>
          {whiteSnusNicotinePerGramError ||
            'Obligatoriskt för att aktivera produkten, men kan sparas som utkast utan att fyllas i.'}
        </FormHelperText>
      </div>

      <div style={{ width: '100%', marginBottom: '5px' }}>
        <InputLabel heading="Nikotinstyrka" text="Beskrivning av nikotinstyrkan, t.ex. 'Stark', 'Medium', 'Svag'." />
        <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
          <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center', flexGrow: 1 }}>
            <InputTextField<IProduct>
              readOnly={readOnly}
              label="Ex. Stark"
              inputName="whiteSnusNicotineStrength"
              value={whiteSnusNicotineStrength}
              onChange={event => onNicotineStrengthChange(event.target.value)}
              style={styles}
            />
          </div>
          {approve && state?.approve?.find(x => x.field === 'whiteSnusNicotineStrength') && (
            <ThreeWaySwitch
              readOnly={readOnly}
              name='whiteSnusNicotineStrength'
              state={state}
              setState={setState}
            />
          )}
        </div>
      </div>

      <div style={{ width: '100%', marginBottom: '5px' }}>
        <InputLabel heading="Smak" text="Produktens smak eller smakprofil." />
        <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
          <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center', flexGrow: 1 }}>
            <InputTextField<IProduct>
              readOnly={readOnly}
              label="Ex. Mint, Citrus"
              inputName="whiteSnusFlavor"
              value={whiteSnusFlavor}
              onChange={event => onFlavorChange(event.target.value)}
              style={styles}
            />
          </div>
          {approve && state?.approve?.find(x => x.field === 'whiteSnusFlavor') && (
            <ThreeWaySwitch
              readOnly={readOnly}
              name='whiteSnusFlavor'
              state={state}
              setState={setState}
            />
          )}
        </div>
      </div>

      <div style={{ width: '100%', marginBottom: '5px' }}>
        <InputLabel heading="Antal portioner/dosa" text="Antal portioner per dosa/förpackning." />
        <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
          <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center', flexGrow: 1 }}>
            <FloatInput<IProduct>
              readOnly={readOnly}
              dense={false}
              state={state}
              label="Antal portioner/dosa"
              name="whiteSnusPortionsPerUnit"
              value={whiteSnusPortionsPerUnit}
              onChange={event => onPortionsPerUnitChange(event.target.value)}
              style={styles}
            />
            <h3 style={{ margin: 0 }}>&nbsp;st</h3>
          </div>
          {approve && state?.approve?.find(x => x.field === 'whiteSnusPortionsPerUnit') && (
            <ThreeWaySwitch
              readOnly={readOnly}
              name='whiteSnusPortionsPerUnit'
              state={state}
              setState={setState}
            />
          )}
        </div>
      </div>

      <div style={{ width: '100%', marginBottom: '5px' }}>
        <InputLabel heading="Vikt per portion" text="Vikt per portion angivet i gram." />
        <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
          <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center', flexGrow: 1 }}>
            <FloatInput<IProduct>
              readOnly={readOnly}
              state={state}
              dense={false}
              label="Vikt per portion"
              name="whiteSnusWeightPerPortion"
              value={whiteSnusWeightPerPortion}
              onChange={event => onWeightPerPortionChange(event.target.value)}
              style={styles}
            />
            <h3 style={{ margin: 0 }}>&nbsp;g</h3>
          </div>
          {approve && state?.approve?.find(x => x.field === 'whiteSnusWeightPerPortion') && (
            <ThreeWaySwitch
              readOnly={readOnly}
              name='whiteSnusWeightPerPortion'
              state={state}
              setState={setState}
            />
          )}
        </div>
      </div>

      <div style={{ width: '100%', marginBottom: '5px' }}>
        <InputLabel heading="Format" text="Produktens format, t.ex. Slim, Mini, Large, Normal." />
        <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
          <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center', flexGrow: 1 }}>
            <InputTextField<IProduct>
              readOnly={readOnly}
              label="Ex. Slim, Mini, Large"
              inputName="whiteSnusFormat"
              value={whiteSnusFormat}
              onChange={event => onFormatChange(event.target.value)}
              style={styles}
            />
          </div>
          {approve && state?.approve?.find(x => x.field === 'whiteSnusFormat') && (
            <ThreeWaySwitch
              readOnly={readOnly}
              name='whiteSnusFormat'
              state={state}
              setState={setState}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default WhiteSnus;
